import { useTheme } from '@mui/material';
import { useCallback } from 'react';
import { useGiftContext } from './Context/GiftContextProvider';
import DSBox from '../../design-system/DSBox';
import DSImageList from '../../design-system/DSImageList';
import DSImageListItem from '../../design-system/DSImageListItem';
import DSImageListItemBar from '../../design-system/DSImageListItemBar';
import { GiftCategory } from '../../api/gift';
import { KLYDO_YELLOW } from '../../consts/colors';
import { isMobile } from 'react-device-detect';
import { isCustomPendulumLogo } from './utils';

const GiftOccasion = () => {
  const {
    setSelectedOccasion,
    selectedOccasion,
    giftCategories,
    next,
    setKlydoGiftProps,
    klydoGiftProps,
  } = useGiftContext();
  const theme = useTheme();

  const handleSelectOccasion = useCallback(
    (giftCategory: GiftCategory) => {
      setSelectedOccasion(giftCategory);
      const isCustomLogo = isCustomPendulumLogo(
        klydoGiftProps?.pendulumLogoUrl,
        giftCategories,
      );
      // A custom logo is kept. Otherwise always set (or clear) pendulumLogoUrl
      // so that going back and selecting a different category doesn't leave a
      // stale pendulumLogoUrl from the previous one.
      // The type goes with it: a category logo is an icon, so a 'full' type left
      // over from the admin form would draw that icon as a full bob.
      if (!isCustomLogo) {
        setKlydoGiftProps({
          pendulumLogoUrl: giftCategory?.pendulumLogoUrl?.trim() || undefined,
          pendulumLogoType: 'sized',
        });
      }
      next();
    },
    [
      setSelectedOccasion,
      setKlydoGiftProps,
      next,
      klydoGiftProps,
      giftCategories,
    ],
  );

  return (
    <DSBox
      sx={{
        paddingTop: { xs: 1, md: 5 },
        justifyContent: 'center',
        display: 'flex',
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <DSImageList
        cols={isMobile ? 2 : 5}
        gap={isMobile ? 10 : 20}
        sx={{ width: { xs: 300, md: 800 } }}
      >
        {(giftCategories ?? []).map((giftCategory) => (
          <DSImageListItem
            key={giftCategory.name}
            onClick={() => handleSelectOccasion(giftCategory)}
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              display: 'flex',
              cursor: 'pointer',
            }}
          >
            <img
              src={giftCategory.categoryImageUrl}
              alt={giftCategory.name}
              loading="eager"
              style={{
                objectFit: 'none',
                border: `5px solid ${
                  giftCategory.name === selectedOccasion?.name
                    ? KLYDO_YELLOW
                    : 'transparent'
                }`,
                width: 120,
                height: 120,
                borderRadius: '20%',
                backgroundColor: '#FFFFFF',
              }}
            />
            <DSImageListItemBar
              position="below"
              title={giftCategory.name}
              sx={{
                '& .MuiImageListItemBar-title': {
                  fontSize: '15px',
                  fontWeight: 400,
                  fontFamily: 'Gilroy-Bold',
                  letterSpacing: '0.01em',
                  color:
                    giftCategory.name === selectedOccasion?.name
                      ? KLYDO_YELLOW
                      : 'inherit',
                },
              }}
            />
          </DSImageListItem>
        ))}
      </DSImageList>
    </DSBox>
  );
};

export default GiftOccasion;
